import type { ReactNode } from 'react';
import { createContext, use, useCallback, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Snackbar, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import WarningAmber from '@mui/icons-material/WarningAmber';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Close from '@mui/icons-material/Close';
import { resolveTone } from './statusSeverity';

export type ToastSeverity = 'success' | 'error' | 'info' | 'warning';

const TOAST_ICONS: Record<ToastSeverity, typeof CheckCircleOutlined> = {
  success: CheckCircleOutlined,
  error: ErrorOutlineOutlined,
  warning: WarningAmber,
  info: InfoOutlined,
};

export interface ToastOptions {
  severity?: ToastSeverity;
  title?: ReactNode;
  message?: ReactNode;
  /** Auto-dismiss delay in ms. Defaults to 5000; pass 0 to keep it sticky. */
  duration?: number;
}

interface ActiveToast extends ToastOptions {
  id: number;
  severity: ToastSeverity;
}

export interface ToastApi {
  /** Push a toast; returns its id. */
  show: (options: ToastOptions) => number;
  success: (title: ReactNode, message?: ReactNode) => number;
  error: (title: ReactNode, message?: ReactNode) => number;
  info: (title: ReactNode, message?: ReactNode) => number;
  /** Dismiss a toast early by id. */
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_DURATION = 5000;

/**
 * A single toast: a white card with a colored left rail keyed to the tone, a
 * leading tone icon, title, message, and a dismiss button. The Polaris kit
 * treatment (`kit/Overlays.jsx` `Toast`).
 */
function ToastCard({ toast, onDismiss }: { toast: ActiveToast; onDismiss: (id: number) => void }) {
  const theme = useTheme();
  const tone = resolveTone(theme, toast.severity);
  const Icon = TOAST_ICONS[toast.severity];

  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        width: 340,
        maxWidth: '100%',
        p: '12px 14px',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: tone.main,
        borderRadius: '6px',
        boxShadow: '0 8px 24px rgba(4,29,51,0.16)',
      }}
    >
      <Icon sx={{ fontSize: 18, color: tone.main, mt: '1px', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {toast.title && (
          <Typography component="div" sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
            {toast.title}
          </Typography>
        )}
        {toast.message && (
          <Typography
            component="div"
            sx={{ fontSize: 12, fontWeight: 400, color: 'text.secondary', mt: 0.25 }}
          >
            {toast.message}
          </Typography>
        )}
      </Box>
      <IconButton
        aria-label="Dismiss"
        size="small"
        onClick={() => onDismiss(toast.id)}
        sx={{ color: 'text.secondary', flexShrink: 0, p: 0.25 }}
      >
        <Close sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}

/**
 * App-level toast host on MUI `Snackbar`, anchored bottom-right. Each toast
 * renders as a white card with a tone-colored left rail. Wrap the app once;
 * call {@link useToast} anywhere to push success/error/info toasts. Multiple
 * toasts stack. Copy is caller-supplied so toasts stay i18n-agnostic.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      const toast: ActiveToast = { severity: 'info', ...options, id };
      setToasts((current) => [...current, toast]);
      const duration = options.duration ?? DEFAULT_DURATION;
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (title, message) => show({ severity: 'success', title, message }),
      error: (title, message) => show({ severity: 'error', title, message }),
      info: (title, message) => show({ severity: 'info', title, message }),
    }),
    [show, dismiss],
  );

  return (
    <ToastContext value={api}>
      {children}
      <Snackbar open={toasts.length > 0} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Stack spacing={1.25} sx={{ width: 340, maxWidth: '100%' }}>
          {toasts.map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </Stack>
      </Snackbar>
    </ToastContext>
  );
}

/** Access the toast API. Must be called under a {@link ToastProvider}. */
export function useToast(): ToastApi {
  const api = use(ToastContext);
  if (!api) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return api;
}
